/**
 * 头像使用这个链接
 * https://r.hrc.woa.com/photo/${图片尺寸}/lynnhong.png
 * 例子：https://r.hrc.woa.com/photo/48/lynnhong.png
 * 这个图片链接支持尺寸500/150/100/48，大图用150，目前大部分只使用到48
 */

import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import SpinLoadingIcon from '@strategic/assests/images/loading.svg';
import type { IBaseProps } from '@home/types/common';
import './index.scss';
import DEFAULT_USER_ICON from '@home/assets/img/common/user-avatar-default.svg';
import DEFAULT_GAME_ICON from '@/pages/dashboard/assets/svg/mobile-game-default.svg';
import DEFAULT_STUDIO_ICON from '@gp/assets/svg/company-default.svg';
import BACKGROUND_IMG_DEFAULT_ICON from '@/pages/home/assets/img/common/video-default.png';
export type IGeneralImageDomain = 'user' | 'game' | 'studio';

export const PLACEHOLDER_COVER =
  '//imgcache.qq.com/open_proj/proj_qcloud_v2/tea-style/dist/assets/image/upload-thumb.png';
export const BACKGROUND_IMG_DEFAULT = BACKGROUND_IMG_DEFAULT_ICON;
interface IImageNewProps extends IBaseProps {
  // 图片标签的内联样式
  imgStyle?: React.CSSProperties;
  // 图片标签的className
  imgClassName?: string;
  // 图片的border-radius
  borderRadius?: number | string;
  // 图片domain、人员、游戏、studio等，目前主要用于区分展示不同的兜底图片
  domain?: IGeneralImageDomain;
  // 图片宽度
  width?: number;
  // 图片高度
  height?: number;
  // 图片src链接
  src: string;
  // 加载异常时展示的图片
  errorImg?: string;
  needBorderRadius?: boolean;
  // click事件回调
  onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

// 兜底图片映射
const DEFAULT_IMG: Record<IGeneralImageDomain, string> = {
  user: DEFAULT_USER_ICON,
  game: DEFAULT_GAME_ICON,
  studio: DEFAULT_STUDIO_ICON,
};

const StrategicImageNew: React.FC<IImageNewProps> = (props) => {
  const {
    style,
    className,
    imgStyle,
    imgClassName,
    borderRadius,
    domain = 'game',
    width = 24,
    height = 24,
    src,
    errorImg: errorImgProps,
    onClick,
    needBorderRadius = true,
    ...remains
  } = props;

  // 加载异常时，展示的默认图片
  const errorImg = errorImgProps ?? DEFAULT_IMG[domain] ?? DEFAULT_IMG.game;

  const [isLoading, setIsLoading] = useState(true);

  const [isError, setIsError] = useState(false);

  const handleLoadedSuccess = () => {
    setIsLoading(false);
    setIsError(false);
  };

  const handleLoadedFailed = () => {
    setIsLoading(false);
    setIsError(true);
  };

  useEffect(() => {
    if (!src) {
      setIsLoading(false);
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setIsError(false);
  }, [src]);

  return (
    <div
      style={{ ...style, width, height, borderRadius }}
      className={classnames('dbg-general-image-comp', className, {
        'is-loading': isLoading,
        'dbg-isneed-border-radius': needBorderRadius,
      })}
      onClick={onClick}
    >
      {isLoading && (
        <div className="dbg-general-image-placeholder-container">
          <img className="dbg-general-image-loading" src={SpinLoadingIcon} width={16} height={16} />
        </div>
      )}
      {!isLoading && isError && (
        <div className="dbg-general-image-placeholder-container dbg-general-image-placeholder-container--error">
          <img className="dbg-general-image-error" src={errorImg} width={width} height={height} />
        </div>
      )}
      <img
        {...remains}
        style={imgStyle}
        className={classnames('dbg-general-image', imgClassName, {
          'is-loading': isLoading,
          'is-error': isError,
        })}
        // 解决跨域：第三方图片存在防盗链，不发送referrer
        referrerPolicy="no-referrer"
        width={width}
        height={height}
        src={src}
        onLoad={handleLoadedSuccess}
        onError={handleLoadedFailed}
      />
    </div>
  );
};

export default React.memo(StrategicImageNew);
